import API from '../config/api';
import React, { useState, useRef, useEffect } from 'react';

import axios from 'axios';

import { Link } from 'react-router-dom';

const WELCOME = {
  role: 'bot',
  text: "Hi! I'm ShopGenie Support 🧞 I can help with orders, payments, refunds, returns and account issues — and help you raise a support ticket. How can I help?",
  products: []
};

const SUGGESTIONS = [
  "My order hasn't arrived",
  'I want a refund',
  'Payment failed but money deducted',
  'How do I raise a ticket?'
];

function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const bodyRef = useRef(null);

  /* Allow the mobile speed-dial to open this chat */
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('open-help-chat', openHandler);
    return () => window.removeEventListener('open-help-chat', openHandler);
  }, []);

  /* Tell the mobile speed-dial to hide itself while the chat is open so its
     floating button doesn't cover the chat's send button. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('help-chat-toggle', { detail: open }));
  }, [open]);

  /* Keep the conversation scrolled to the latest message */
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();

    if (!text || loading) {
      return;
    }

    const userMsg = { role: 'user', text };
    const nextMessages = [...messages, userMsg];

    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      /* Send prior turns (excluding the canned welcome) for context */
      const history = nextMessages
        .filter((m, i) => !(i === 0 && m === WELCOME))
        .map((m) => ({ role: m.role, text: m.text }));

      const { data } = await axios.post(`${API}/ai/chat`, {
        message: text,
        history,
        mode: 'help'
      });

      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: data.reply,
          products: data.products || [],
          support: data.support || null,
          openAssistant: !!data.openAssistant
        }
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Sorry, I could not reach the assistant. Please try again.',
          products: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        className="chatbot-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open ShopGenie assistant"
      >
        <span className="chatbot-launcher-orb">
          {/* Halo rings radiating outward — the "divine" cue, and the thing
              that draws the eye to an otherwise static corner button. */}
          <span className="chatbot-halo" />
          <span className="chatbot-halo chatbot-halo-2" />

          <span className="chatbot-launcher-icon">
            <span className={`chatbot-icon-face ${open ? 'is-open' : ''}`}>
              {open ? '✕' : '💬'}
            </span>
          </span>

          <span className="chatbot-spark chatbot-spark-1">✦</span>
          <span className="chatbot-spark chatbot-spark-2">✦</span>
          <span className="chatbot-spark chatbot-spark-3">✧</span>
        </span>

        {!open && <span className="chatbot-launcher-name">Help</span>}
      </button>

      {open && (
        <div className="chatbot-panel">
          {/* Header */}
          <div className="chatbot-header">
            {/* Slow-drifting colour blobs behind the header gradient */}
            <span className="chatbot-header-blob chatbot-header-blob-1" />
            <span className="chatbot-header-blob chatbot-header-blob-2" />
            <span className="chatbot-header-shine" />

            <div className="chatbot-header-id">
              <span className="chatbot-avatar">
                <span className="chatbot-avatar-ring" />
                <span className="chatbot-avatar-face">🧞</span>
              </span>
              <div>
                <div className="chatbot-title">ShopGenie Support</div>
                <div className="chatbot-status">
                  <span className="chatbot-dot" />
                  Orders, refunds &amp; tickets
                </div>
              </div>
            </div>

            <button
              type="button"
              className="chatbot-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`chatbot-msg ${m.role === 'user' ? 'user' : 'bot'}`}
              >
                <div className="chatbot-bubble">{m.text}</div>

                {m.products && m.products.length > 0 && (
                  <div className="chatbot-products">
                    {m.products.map((p) => (
                      <Link
                        key={p._id}
                        to={`/product/${p._id}`}
                        className="chatbot-product-card"
                        onClick={() => setOpen(false)}
                      >
                        <img src={p.image} alt={p.name} />
                        <div className="chatbot-product-info">
                          <div className="chatbot-product-name">{p.name}</div>
                          <div className="chatbot-product-price">
                            ₹{p.price}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {m.support && m.support.raiseTicket && (
                  <div className="chatbot-support">
                    <Link
                      to="/help"
                      className="chatbot-support-btn primary"
                      onClick={() => setOpen(false)}
                    >
                      🎫 Raise a Support Ticket
                      {m.support.category ? ` · ${m.support.category}` : ''}
                    </Link>
                    <Link
                      to="/my-tickets"
                      className="chatbot-support-btn"
                      onClick={() => setOpen(false)}
                    >
                      📋 Track My Tickets
                    </Link>
                  </div>
                )}

                {m.openAssistant && (
                  <div className="chatbot-support">
                    <Link
                      to="/assistant"
                      className="chatbot-support-btn primary"
                      onClick={() => setOpen(false)}
                    >
                      🛍️ Open AI Shopping Assistant
                    </Link>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="chatbot-msg bot">
                <div className="chatbot-bubble chatbot-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* Starter suggestions (only before the first user message) */}
            {messages.length === 1 && !loading && (
              <div className="chatbot-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="chatbot-chip"
                    onClick={() => sendMessage(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="chatbot-input">
            <textarea
              rows={1}
              placeholder="Ask about an order, payment or refund..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
            />
            <button
              type="button"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              aria-label="Send"
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <style>{chatbotStyles}</style>
    </>
  );
}

const chatbotStyles = `
  /* ===================== Launcher =====================
     A resting corner button gets ignored, so it breathes, haloes and
     sparkles on a slow loop — enough to catch the eye, slow enough not to
     nag. Everything here is transform/opacity only, so it never triggers
     layout while idling on every page. */
  .chatbot-launcher {
    position: fixed;
    bottom: 22px;
    right: 22px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    z-index: 1050;
  }
  /* On mobile the speed-dial (MobileFabDial) opens this chat instead */
  @media (max-width: 1199.98px) {
    .chatbot-launcher { display: none !important; }
  }

  .chatbot-launcher-orb {
    position: relative;
    width: 60px;
    height: 60px;
    display: grid;
    place-items: center;
    animation: chatbotFloat 3.6s ease-in-out infinite;
  }
  @keyframes chatbotFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-5px); }
  }

  /* Expanding halo rings */
  .chatbot-halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(245, 158, 11, 0.6);
    opacity: 0;
    pointer-events: none;
    animation: chatbotHalo 2.8s ease-out infinite;
  }
  .chatbot-halo-2 { animation-delay: 1.4s; }
  @keyframes chatbotHalo {
    0%   { opacity: 0.85; transform: scale(1); border-width: 2px; }
    70%  { opacity: 0.12; }
    100% { opacity: 0; transform: scale(1.85); border-width: 0.5px; }
  }

  .chatbot-launcher-icon {
    position: relative;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    /* Divine maroon → saffron with a golden halo + gold ring */
    background:
      radial-gradient(circle at 50% 35%, rgba(253,230,138,0.6), transparent 62%),
      linear-gradient(135deg, #7c2d12, #ea580c);
    border: 2px solid rgba(253,230,138,0.75);
    color: #fff;
    font-size: 26px;
    display: grid;
    place-items: center;
    box-shadow: 0 10px 26px rgba(180, 83, 9, 0.45), 0 0 16px rgba(245,158,11,0.4);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.25s ease;
    overflow: hidden;
  }
  /* Light sweeping across the orb */
  .chatbot-launcher-icon::after {
    content: '';
    position: absolute;
    top: -60%;
    left: -120%;
    width: 60%;
    height: 220%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent);
    transform: skewX(-20deg);
    animation: chatbotSheen 4.5s ease-in-out infinite;
  }
  @keyframes chatbotSheen {
    0%, 62%  { left: -120%; }
    84%      { left: 160%; }
    100%     { left: 160%; }
  }
  .chatbot-launcher:hover .chatbot-launcher-icon {
    transform: scale(1.12);
    box-shadow: 0 14px 34px rgba(180, 83, 9, 0.55), 0 0 26px rgba(245,158,11,0.7);
  }
  .chatbot-launcher:active .chatbot-launcher-icon {
    transform: scale(0.96);
  }

  /* 💬 and ✕ swap with a spin rather than a hard cut */
  .chatbot-icon-face {
    display: block;
    line-height: 1;
    animation: chatbotIconIn 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .chatbot-icon-face.is-open { font-size: 22px; }
  @keyframes chatbotIconIn {
    from { opacity: 0; transform: rotate(-120deg) scale(0.4); }
    to   { opacity: 1; transform: rotate(0) scale(1); }
  }

  /* Sparks drifting off the orb */
  .chatbot-spark {
    position: absolute;
    font-size: 11px;
    color: #fde68a;
    opacity: 0;
    pointer-events: none;
    text-shadow: 0 0 6px rgba(253, 224, 71, 0.9);
    animation: chatbotSpark 3.2s ease-out infinite;
  }
  .chatbot-spark-1 { top: 2px;  right: 2px;  animation-delay: 0.2s; }
  .chatbot-spark-2 { bottom: 4px; left: 0px;  animation-delay: 1.3s; }
  .chatbot-spark-3 { top: 6px;  left: 4px;   animation-delay: 2.2s; font-size: 9px; }
  @keyframes chatbotSpark {
    0%   { opacity: 0; transform: translate(0,0) scale(0.4) rotate(0deg); }
    18%  { opacity: 1; transform: translate(4px,-6px) scale(1) rotate(45deg); }
    55%  { opacity: 0.7; }
    100% { opacity: 0; transform: translate(10px,-18px) scale(0.3) rotate(180deg); }
  }

  .chatbot-launcher-name {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.3px;
    color: #b45309;
    background: #fff;
    padding: 2px 10px;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(180, 83, 9, 0.25);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .chatbot-launcher:hover .chatbot-launcher-name {
    transform: translateY(2px);
    background: #fffbeb;
  }

  /* ===================== Panel ===================== */
  .chatbot-panel {
    position: fixed;
    bottom: 94px;
    right: 22px;
    width: min(380px, calc(100vw - 32px));
    height: min(560px, calc(100vh - 130px));
    background: #fff;
    border-radius: 22px;
    box-shadow:
      0 30px 70px rgba(60, 20, 5, 0.32),
      0 0 0 1px rgba(253, 230, 138, 0.5);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 1050;
    /* grows out of the launcher corner rather than just fading in */
    transform-origin: 100% 100%;
    animation: chatbotPop 0.42s cubic-bezier(0.22, 1.2, 0.36, 1);
  }
  @keyframes chatbotPop {
    0%   { opacity: 0; transform: translateY(24px) scale(0.82); }
    60%  { opacity: 1; }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ===================== Header ===================== */
  .chatbot-header {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #3b0764, #7c2d12 55%, #ea580c);
    background-size: 200% 200%;
    color: #fff;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    animation: chatbotHeaderShift 12s ease-in-out infinite;
  }
  /* The gradient drifts, so the header is never quite static */
  @keyframes chatbotHeaderShift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }
  .chatbot-header-id {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .chatbot-title {
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.2;
  }

  /* Soft blobs drifting behind the header */
  .chatbot-header-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(26px);
    pointer-events: none;
    opacity: 0.5;
  }
  .chatbot-header-blob-1 {
    width: 120px; height: 120px;
    background: #f59e0b;
    top: -60px; left: 12%;
    animation: chatbotBlob1 9s ease-in-out infinite;
  }
  .chatbot-header-blob-2 {
    width: 100px; height: 100px;
    background: #a855f7;
    bottom: -60px; right: 10%;
    opacity: 0.4;
    animation: chatbotBlob2 11s ease-in-out infinite;
  }
  @keyframes chatbotBlob1 {
    0%, 100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(26px, 12px) scale(1.15); }
  }
  @keyframes chatbotBlob2 {
    0%, 100% { transform: translate(0,0) scale(1); }
    50%      { transform: translate(-22px, -10px) scale(1.2); }
  }

  /* Gold thread under the header, with a shine travelling along it */
  .chatbot-header::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg,
      transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
  }
  .chatbot-header-shine {
    position: absolute;
    bottom: 0;
    left: -30%;
    width: 30%;
    height: 2px;
    z-index: 4;
    background: linear-gradient(90deg, transparent, #fffbeb, transparent);
    animation: chatbotThread 3.4s ease-in-out infinite;
  }
  @keyframes chatbotThread {
    0%       { left: -30%; opacity: 0; }
    15%      { opacity: 1; }
    85%      { opacity: 1; }
    100%     { left: 100%; opacity: 0; }
  }

  .chatbot-avatar {
    position: relative;
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    background:
      radial-gradient(circle at 50% 38%, rgba(253,230,138,0.6), transparent 62%),
      linear-gradient(135deg, #7c2d12, #ea580c);
    border: 1.5px solid rgba(253,230,138,0.7);
    border-radius: 50%;
    font-size: 20px;
    box-shadow: 0 0 12px rgba(245,158,11,0.5);
    flex-shrink: 0;
  }
  .chatbot-avatar-face {
    display: block;
    line-height: 1;
    /* positioned, so it paints above the absolutely-positioned halo ring
       rather than under it */
    position: relative;
    z-index: 1;
    animation: chatbotGenie 5s ease-in-out infinite;
  }
  /* The genie bobs and tilts, as if hovering */
  @keyframes chatbotGenie {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25%      { transform: translateY(-2px) rotate(-7deg); }
    60%      { transform: translateY(1px) rotate(6deg); }
  }
  /* Rotating gold halo around the avatar */
  .chatbot-avatar-ring {
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: conic-gradient(from 0deg,
      transparent, rgba(253,224,71,0.9), transparent 45%,
      transparent, rgba(253,224,71,0.7), transparent 95%);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px));
    animation: chatbotSpin 4s linear infinite;
    pointer-events: none;
  }
  @keyframes chatbotSpin {
    to { transform: rotate(360deg); }
  }

  .chatbot-status {
    font-size: 0.72rem;
    opacity: 0.85;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  /* Live "we're online" pulse */
  .chatbot-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4ade80;
    box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.8);
    animation: chatbotPulse 2s ease-out infinite;
    flex-shrink: 0;
  }
  @keyframes chatbotPulse {
    0%   { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.75); }
    70%  { box-shadow: 0 0 0 7px rgba(74, 222, 128, 0); }
    100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
  }

  .chatbot-close {
    position: relative;
    z-index: 2;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.18);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: #fff;
    font-size: 13px;
    cursor: pointer;
    opacity: 0.9;
    transition: transform 0.25s ease, background 0.2s ease, opacity 0.2s ease;
  }
  .chatbot-close:hover {
    opacity: 1;
    background: rgba(255,255,255,0.25);
    transform: rotate(90deg);
  }

  /* ===================== Messages ===================== */
  .chatbot-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background:
      radial-gradient(420px 200px at 12% 0%, rgba(245, 158, 11, 0.09), transparent 70%),
      radial-gradient(420px 220px at 88% 100%, rgba(124, 45, 18, 0.08), transparent 70%),
      #fdfaf6;
    scroll-behavior: smooth;
  }
  .chatbot-body::-webkit-scrollbar { width: 6px; }
  .chatbot-body::-webkit-scrollbar-thumb {
    background: #fcd34d;
    border-radius: 10px;
  }
  .chatbot-body::-webkit-scrollbar-track { background: transparent; }

  .chatbot-msg {
    margin-bottom: 14px;
    display: flex;
    flex-direction: column;
  }
  .chatbot-msg.user { align-items: flex-end; }
  .chatbot-msg.bot  { align-items: flex-start; }

  .chatbot-bubble {
    position: relative;
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 0.9rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-wrap: break-word;
    /* Each new message arrives with a little spring */
    animation: chatbotBubbleIn 0.4s cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
  }
  .chatbot-msg.user .chatbot-bubble {
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 6px 16px rgba(234, 88, 12, 0.28);
    animation-name: chatbotBubbleInRight;
  }
  .chatbot-msg.bot .chatbot-bubble {
    background: #fff;
    color: #1e293b;
    border: 1px solid #f1e4cf;
    border-bottom-left-radius: 4px;
    box-shadow: 0 6px 16px rgba(124, 45, 18, 0.08);
  }
  @keyframes chatbotBubbleIn {
    from { opacity: 0; transform: translate(-14px, 8px) scale(0.94); }
    to   { opacity: 1; transform: translate(0, 0) scale(1); }
  }
  @keyframes chatbotBubbleInRight {
    from { opacity: 0; transform: translate(14px, 8px) scale(0.94); }
    to   { opacity: 1; transform: translate(0, 0) scale(1); }
  }

  /* ===================== Product cards ===================== */
  .chatbot-products {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    width: 85%;
  }
  .chatbot-product-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    background: #fff;
    border: 1px solid #f1e4cf;
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.2s ease, border-color 0.2s ease;
    /* staggered so a list of results cascades in */
    animation: chatbotCardIn 0.42s ease backwards;
  }
  .chatbot-product-card:nth-child(1) { animation-delay: 0.05s; }
  .chatbot-product-card:nth-child(2) { animation-delay: 0.12s; }
  .chatbot-product-card:nth-child(3) { animation-delay: 0.19s; }
  .chatbot-product-card:nth-child(4) { animation-delay: 0.26s; }
  .chatbot-product-card:nth-child(n+5) { animation-delay: 0.33s; }
  @keyframes chatbotCardIn {
    from { opacity: 0; transform: translateX(-16px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .chatbot-product-card:hover {
    transform: translateY(-3px) scale(1.015);
    border-color: #fcd34d;
    box-shadow: 0 12px 24px rgba(180, 83, 9, 0.18);
  }
  .chatbot-product-card img {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 8px;
    flex-shrink: 0;
    transition: transform 0.25s ease;
  }
  .chatbot-product-card:hover img { transform: scale(1.08) rotate(-2deg); }
  /* min-width:0 lets a long product name ellipsize instead of stretching
     the card past its container */
  .chatbot-product-info {
    min-width: 0;
  }
  .chatbot-product-name {
    font-size: 0.82rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chatbot-product-price {
    font-size: 0.85rem;
    font-weight: 700;
    color: #16a34a;
  }

  /* ===================== Support buttons ===================== */
  .chatbot-support {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    width: 85%;
  }
  .chatbot-support-btn {
    position: relative;
    overflow: hidden;
    display: block;
    text-align: center;
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 700;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid #f1e4cf;
    background: #fff;
    color: #334155;
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.2s ease;
    animation: chatbotCardIn 0.42s ease backwards;
    animation-delay: 0.08s;
  }
  .chatbot-support-btn.primary {
    background: linear-gradient(135deg, #3b0764, #7c2d12 55%, #ea580c);
    color: #fff;
    border: none;
  }
  /* Shine sweeping across the primary action, drawing the click */
  .chatbot-support-btn.primary::after {
    content: '';
    position: absolute;
    top: 0;
    left: -120%;
    width: 45%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent);
    transform: skewX(-20deg);
    animation: chatbotBtnSheen 3.6s ease-in-out infinite;
  }
  @keyframes chatbotBtnSheen {
    0%, 55% { left: -120%; }
    85%     { left: 160%; }
    100%    { left: 160%; }
  }
  .chatbot-support-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(124, 45, 18, 0.22);
  }

  /* ===================== Suggestion chips ===================== */
  .chatbot-suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
  }
  .chatbot-chip {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #b45309;
    border: 1px solid #fcd34d;
    border-radius: 20px;
    padding: 7px 12px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.2s ease, color 0.2s ease;
    animation: chatbotChipIn 0.45s cubic-bezier(0.22, 1.2, 0.36, 1) backwards;
  }
  .chatbot-chip:nth-child(1) { animation-delay: 0.10s; }
  .chatbot-chip:nth-child(2) { animation-delay: 0.18s; }
  .chatbot-chip:nth-child(3) { animation-delay: 0.26s; }
  .chatbot-chip:nth-child(4) { animation-delay: 0.34s; }
  @keyframes chatbotChipIn {
    from { opacity: 0; transform: translateY(10px) scale(0.9); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .chatbot-chip:hover {
    transform: translateY(-2px) scale(1.04);
    color: #7c2d12;
    box-shadow: 0 8px 18px rgba(245, 158, 11, 0.35);
  }

  /* ===================== Typing ===================== */
  .chatbot-typing {
    display: inline-flex;
    gap: 5px;
    align-items: center;
  }
  .chatbot-typing span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    /* bouncing reads as "thinking" far better than fading */
    animation: chatbotBounce 1.1s infinite ease-in-out;
  }
  .chatbot-typing span:nth-child(2) { animation-delay: 0.15s; }
  .chatbot-typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes chatbotBounce {
    0%, 70%, 100% { transform: translateY(0) scale(1);      opacity: 0.45; }
    35%           { transform: translateY(-6px) scale(1.15); opacity: 1; }
  }

  /* ===================== Input ===================== */
  .chatbot-input {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid #f1e4cf;
    background: #fff;
  }
  .chatbot-input textarea {
    flex: 1;
    resize: none;
    border: 1px solid #ece0cc;
    border-radius: 14px;
    padding: 9px 12px;
    font-size: 0.88rem;
    max-height: 90px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .chatbot-input textarea:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18);
  }
  .chatbot-input button {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    background-size: 160% 160%;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: 0 6px 16px rgba(234, 88, 12, 0.35);
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.2s ease, background-position 0.3s ease;
  }
  .chatbot-input button:not(:disabled):hover {
    transform: scale(1.12) rotate(8deg);
    background-position: right center;
    box-shadow: 0 10px 22px rgba(234, 88, 12, 0.5);
  }
  .chatbot-input button:not(:disabled):active {
    transform: scale(0.94);
  }
  .chatbot-input button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  /* Everything above is decoration — with motion reduced it all stops, and
     the chat still works exactly the same. */
  @media (prefers-reduced-motion: reduce) {
    .chatbot-launcher-orb,
    .chatbot-halo,
    .chatbot-spark,
    .chatbot-launcher-icon::after,
    .chatbot-icon-face,
    .chatbot-header,
    .chatbot-header-blob,
    .chatbot-header-shine,
    .chatbot-avatar-face,
    .chatbot-avatar-ring,
    .chatbot-dot,
    .chatbot-bubble,
    .chatbot-product-card,
    .chatbot-support-btn,
    .chatbot-support-btn.primary::after,
    .chatbot-chip,
    .chatbot-panel {
      animation: none !important;
    }
    .chatbot-halo, .chatbot-spark, .chatbot-header-shine { display: none; }
    .chatbot-body { scroll-behavior: auto; }
  }
`;

export default ChatBot;
